import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { db } from '../db';
import { contentHash } from '../db/contentHash';

// Reason: default adapter works for the `meta` table where PK is `key`;
// entity-specific callers must supply toRecord to set their own PK fields
// and any mirrored index columns (e.g. is_read as 0/1 for articles).
const defaultToRecord = (payload, meta) => ({ ...meta, payload });

/**
 * Local-first read hook.
 *
 * @param {object} opts
 * @param {string} opts.table   - Dexie table name.
 * @param {string|number} opts.key - Primary-key value.
 * @param {number} opts.ttl    - Freshness window in ms.
 * @param {Function} opts.fetch - async ({etag}) => {status, etag, payload}. 304 means no change.
 * @param {Function} [opts.toRecord] - (payload, meta) => row. Callers MUST memoize with useCallback.
 * @returns {{ data, loading, error, refresh, apply }} apply(payload, {etag}) writes a
 *   caller-owned payload to cache + state and bumps the generation so any in-flight fetch is dropped.
 */
export const useLocalFirstQuery = ({ table, key, ttl, fetch, toRecord = defaultToRecord }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Reason: generation counter replaces a single inFlightRef boolean so that a
  // slow fetch for an old key cannot overwrite state or block a new key's fetch.
  const generationRef = useRef(0);

  const runFetch = useCallback(async (cached, gen) => {
    try {
      const resp = await fetch({ etag: cached?.etag ?? null });
      if (gen !== generationRef.current) return;
      const now = Date.now();
      if (resp.status === 304) {
        // Reason: only update fetched_at — row exists by construction (we have a cached row).
        await db.table(table).update(key, { fetched_at: now });
        return;
      }
      if (resp.status !== 200) {
        throw new Error(`Unexpected response status ${resp.status}`);
      }
      const newHash = await contentHash(resp.payload);
      if (gen !== generationRef.current) return;
      if (cached && cached.content_hash === newHash) {
        // Reason: same content, refresh etag + fetched_at without touching payload or state.
        await db.table(table).update(key, { fetched_at: now, etag: resp.etag ?? cached.etag ?? null });
        return;
      }
      const row = toRecord(resp.payload, {
        key,
        etag: resp.etag ?? null,
        fetched_at: now,
        content_hash: newHash,
        previous: cached,
      });
      await db.table(table).put(row);
      setData(resp.payload);
      setError(null);
    } catch (err) {
      if (gen !== generationRef.current) return;
      if (!cached) {
        // No fallback — surface the error.
        setError(err);
        setData(null);
      }
      // else: silent — stale cache stays on screen.
    } finally {
      if (gen === generationRef.current) {
        setLoading(false);
      }
    }
  }, [table, key, fetch, toRecord]);

  useEffect(() => {
    let cancelled = false;
    const gen = ++generationRef.current;
    // Reason: a new key is a fresh query — clear any error left by the previous
    // key's fetch. Offline, an uncached key sets error (no cache fallback); without
    // this reset that stale error leaks into the next key, so a downloaded page
    // visited right after an undownloaded one would render the offline "not
    // downloaded" card despite having a valid cached row.
    setError(null);
    (async () => {
      try {
        const cached = await db.table(table).get(key);
        if (cancelled) return;
        if (cached) {
          setData(cached.payload);
          setLoading(false);
          const isFresh = (Date.now() - cached.fetched_at) < ttl;
          if (!isFresh) runFetch(cached, gen);
        } else {
          setLoading(true);
          await runFetch(null, gen);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err);
          setData(null);
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [table, key, ttl, runFetch]);

  const refresh = useCallback(async () => {
    const gen = ++generationRef.current;
    let cached = null;
    try {
      cached = await db.table(table).get(key);
    } catch {
      // Reason: refresh() is fired without await (useDigestHighlights'
      // settlement watcher, useHighlights, AllHighlights), so a rejection here
      // escapes as an unhandled rejection rather than reaching those callers.
      // Dexie rejects this read when the db is closing, which is routine during
      // teardown. Fall through as "no cached row" so the network revalidation
      // still happens; runFetch owns error surfacing from there.
      cached = null;
    }
    await runFetch(cached ?? null, gen);
  }, [table, key, runFetch]);

  // Reason: imperative escape hatch for caller-owned writes (e.g. a forced
  // regenerate) where the caller already has the fresh payload and wants to
  // handle errors loudly itself rather than via runFetch's silent stale-keep.
  const apply = useCallback(async (payload, { etag = null } = {}) => {
    const gen = ++generationRef.current;
    const now = Date.now();
    const newHash = await contentHash(payload);
    const cached = await db.table(table).get(key);
    const row = toRecord(payload, {
      key,
      etag,
      fetched_at: now,
      content_hash: newHash,
      previous: cached,
    });
    await db.table(table).put(row);
    if (gen === generationRef.current) {
      setData(payload);
      setError(null);
    }
  }, [table, key, toRecord]);

  return useMemo(
    () => ({ data, loading, error, refresh, apply }),
    [data, loading, error, refresh, apply],
  );
};

export default useLocalFirstQuery;
